import {
  collection, doc, getDoc, getDocs, query, where, limit,
} from "firebase/firestore";

import { db } from "../firebase";
import { contentTypeLabel } from "../pages/creator/creatorHelpers";

export async function fetchFanUser(userId) {
  if (!userId) return null;
  const snap = await getDoc(doc(db, "users", userId));
  if (!snap.exists()) return null;
  return { uid: snap.id, ...snap.data() };
}

async function enrichDropItem(row) {
  const dropId = row.dropId || row.momentId || row.postId;
  if (!dropId) return row;

  try {
    const dropSnap = await getDoc(doc(db, "drops", dropId));
    if (!dropSnap.exists()) return row;
    const drop = dropSnap.data();
    let creator = null;
    if (drop.creatorId) {
      const cSnap = await getDoc(doc(db, "users", drop.creatorId));
      if (cSnap.exists()) creator = cSnap.data();
    }
    return {
      ...row,
      dropId,
      title: row.title || row.caption || drop.title,
      thumbnailUrl: row.thumbnailUrl || row.mediaUrl || drop.thumbnailUrl,
      contentType: drop.contentType,
      niche: drop.niche || creator?.niche,
      creatorId: drop.creatorId,
      creatorHandle: row.creatorHandle || row.creatorUsername || creator?.handle || creator?.username,
      creatorName: row.creatorName || creator?.displayName,
      creatorAvatar: row.creatorAvatar || creator?.avatar || creator?.avatarUrl,
      creatorIsCreator: Boolean(creator?.isCreator),
    };
  } catch {
    return row;
  }
}

export async function fetchFanProfileStats(userId) {
  if (!userId) {
    return { following: 0, unlocked: 0, firstViews: 0 };
  }

  const [followsSnap, unlockedSnap, firstViewsSnap] = await Promise.all([
    getDocs(query(collection(db, "follows"), where("followerId", "==", userId))),
    getDocs(query(collection(db, "unlocked_content"), where("userId", "==", userId))),
    getDocs(query(collection(db, "first_views"), where("userId", "==", userId))),
  ]);

  return {
    following: followsSnap.size,
    unlocked: unlockedSnap.size,
    firstViews: firstViewsSnap.size,
  };
}

export async function fetchRecentActivity(userId, max = 20) {
  if (!userId) return [];

  const [firstViewsSnap, unlockedSnap] = await Promise.all([
    getDocs(query(collection(db, "first_views"), where("userId", "==", userId), limit(50))),
    getDocs(query(collection(db, "unlocked_content"), where("userId", "==", userId), limit(50))),
  ]);

  const rows = [
    ...firstViewsSnap.docs.map((d) => ({
      id: d.id,
      type: "firstview",
      ...d.data(),
      activityAt: d.data().viewedAt,
    })),
    ...unlockedSnap.docs.map((d) => ({
      id: d.id,
      type: "unlocked",
      ...d.data(),
      activityAt: d.data().unlockedAt,
    })),
  ]
    .sort((a, b) => (b.activityAt?.seconds || 0) - (a.activityAt?.seconds || 0))
    .slice(0, max);

  const enriched = await Promise.all(rows.map(enrichDropItem));
  return enriched;
}

export function activityMetaLabel(item) {
  const type = contentTypeLabel(item.contentType);
  if (item.type === "firstview" && item.rank) {
    return `${type} · First 100 · #${item.rank} of 100`;
  }
  if (item.type === "unlocked" && item.amountPaid) {
    return `${type} · Unlocked · $${Number(item.amountPaid).toFixed(2)}`;
  }
  return type || "Drop";
}
